import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FolderOpen, Download, ExternalLink, FileText, Code, Link as LinkIcon, Youtube } from 'lucide-react';
import { Button } from '@/components/ui/button';

const resources = [
  { id: 1, title: 'Course Slides - Complete Pack', type: 'PDF', size: '18.4 MB', icon: FileText, color: 'bg-red-100 text-red-600', tag: 'Slides' },
  { id: 2, title: 'Code Repository (GitHub)', type: 'Link', icon: Code, color: 'bg-gray-100 text-gray-700', tag: 'Code', link: '#' },
  { id: 3, title: 'Supplementary Reading List', type: 'PDF', size: '2.1 MB', icon: FileText, color: 'bg-blue-100 text-blue-600', tag: 'Reading' },
  { id: 4, title: 'Recommended YouTube Playlist', type: 'Link', icon: Youtube, color: 'bg-rose-100 text-rose-600', tag: 'Video', link: '#' },
  { id: 5, title: 'AI Tools & APIs Reference Sheet', type: 'PDF', size: '1.4 MB', icon: FileText, color: 'bg-purple-100 text-purple-600', tag: 'Reference' },
  { id: 6, title: 'Community Discord Server', type: 'Link', icon: LinkIcon, color: 'bg-indigo-100 text-indigo-600', tag: 'Community', link: '#' },
  { id: 7, title: 'Interview Prep Questions (200+)', type: 'PDF', size: '3.7 MB', icon: FileText, color: 'bg-green-100 text-green-600', tag: 'Career' },
  { id: 8, title: 'Dataset Collection for Projects', type: 'ZIP', size: '45.2 MB', icon: FolderOpen, color: 'bg-orange-100 text-orange-600', tag: 'Data' },
];

const tagColors = {
  Slides: 'bg-red-100 text-red-600',
  Code: 'bg-gray-100 text-gray-700',
  Reading: 'bg-blue-100 text-blue-600',
  Video: 'bg-rose-100 text-rose-600',
  Reference: 'bg-purple-100 text-purple-600',
  Community: 'bg-indigo-100 text-indigo-600',
  Career: 'bg-green-100 text-green-600',
  Data: 'bg-orange-100 text-orange-600',
};

export default function ResourcesSection() {
  const [filter, setFilter] = useState('All');
  const tags = ['All', ...new Set(resources.map(r => r.tag))];
  const filtered = filter === 'All' ? resources : resources.filter(r => r.tag === filter);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-bold tracking-tight">Resources</h2>
        <p className="text-muted-foreground text-sm mt-1">{resources.length} resources available</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {tags.map(tag => (
          <button key={tag} onClick={() => setFilter(tag)}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${filter === tag ? 'bg-primary text-primary-foreground shadow-md shadow-primary/25' : 'glass border border-black/10 text-muted-foreground hover:text-foreground'}`}>
            {tag}
          </button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {filtered.map((res, i) => {
          const Icon = res.icon;
          return (
            <motion.div key={res.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              whileHover={{ y: -2, transition: { duration: 0.2 } }}
              className="glass rounded-2xl border border-white/80 p-4 flex items-center gap-4 hover:border-primary/20 transition-colors group">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${res.color}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-bold text-sm group-hover:text-primary transition-colors truncate">{res.title}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${tagColors[res.tag]}`}>{res.tag}</span>
                  <span className="text-xs text-muted-foreground">{res.type}{res.size ? ` · ${res.size}` : ''}</span>
                </div>
              </div>
              <Button size="icon" variant="ghost" className="rounded-xl h-8 w-8 flex-shrink-0 hover:bg-primary/10">
                {res.type === 'Link' ? <ExternalLink className="w-4 h-4 text-primary" /> : <Download className="w-4 h-4 text-primary" />}
              </Button>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}